<script lang="ts" setup>
import { Tab, TabGroup, TabList, TabPanel, TabPanels } from '@headlessui/vue'
import { useSearch } from '~/store/search'
import type { NewPayload } from '~/models/Search'

const emit = defineEmits(['setMeta', 'setCanon'])
const route = useRoute()
const router = useRouter()
const { $config } = useNuxtApp()
const searchStore = useSearch()
const searchJobUrl = useCookie('searchJobUrl', { path: '/', maxAge: 3600, domain: $config.public.COOKIE_DOMAIN })
const searchCompUrl = useCookie('searchCompUrl', { path: '/', maxAge: 3600, domain: $config.public.COOKIE_DOMAIN })
const totalSearch = useCookie('totalSearch', { path: '/', maxAge: 3600, domain: $config.public.COOKIE_DOMAIN })
const tabs = ref([
  { label: 'Jobs', total: totalSearch.value ? Number(totalSearch.value.split(',')[0]) : 0, isActive: route.query?.st !== 'companies' },
  { label: 'Companies', total: totalSearch.value ? Number(totalSearch.value.split(',')[1]) : 0, isActive: route.query?.st === 'companies' },
])

if (isEmpty(useGetSubdomain()))
  navigateTo('', { redirectCode: 301 })

async function calculateOtherTotal() {
  const newPayload = (await searchStore.getPayload(route.params, route.query)) as NewPayload
  const [jobResult, companyResult] = await Promise.all([
    useAsyncData('get_job_total', () => searchStore.getJob(newPayload?.filter, $config.public.SEARCHAPI_BASE_URL, $config.SEARCHAPI_KEY, true), { server: true }),
    useAsyncData('get_company_total', () => searchStore.getCompany(newPayload?.filter, $config.public.SEARCHAPI_BASE_URL), { server: true }),
  ])

  if (jobResult)
    tabs.value[0].total = jobResult.data.value.total
  if (companyResult)
    tabs.value[1].total = companyResult.data.value.total

  totalSearch.value = JSON.stringify(`${tabs.value[0].total},${tabs.value[1].total}`)
}

async function changeTab(idx: number) {
  if (idx === 1) {
    // CHANGE TAB TO COMPANY
    setCurrUrl(route.fullPath, 0)
    const extractedJobUrl = searchStore.extractJobInfo(route.fullPath)
    let newUrl = searchCompUrl.value

    if (isEmpty(newUrl))
      newUrl = `/search?st=companies&q=${extractedJobUrl.jobTitle || ''}&location=${extractedJobUrl.location || 'all'}&industry=all&page=1`
    router.push(newUrl)
  }
  else {
    // CHANGE TAB TO JOB
    setCurrUrl(route.fullPath, 1)
    const extractedCompUrl = searchStore.extractJobInfo(route.fullPath)
    let newUrl = searchJobUrl.value

    if (isEmpty(newUrl))
      newUrl = `/search?st=companies&q=${extractedCompUrl.jobTitle || ''}&location=${extractedCompUrl.location || 'all'}&industry=all&page=1`
    router.push(newUrl)
  }
}

function setCurrUrl(value: string, idx: number) {
  if (idx === 0)
    searchJobUrl.value = JSON.stringify(value)
  else
    searchCompUrl.value = JSON.stringify(value)
}

async function setTotal(value: number, idx: number) {
  tabs.value[idx].total = value
  if ((isEmpty(totalSearch.value)))
    await calculateOtherTotal()
  else
    totalSearch.value = JSON.stringify(`${tabs.value[0].total},${tabs.value[1].total}`)
}
</script>

<template>
  <div class="max-w-6xl mx-auto">
    <TabGroup manual :selected-index="tabs[0].isActive ? 0 : 1">
      <TabList class="flex space-x-2 pt-0 lg:pt-4">
        <Tab v-for="(item, idx) in tabs" :key="idx" as="template" @click="changeTab(idx)">
          <button class="px-2 pb-2 pt-2.5 min-w-40 capitalize" :class="[item.isActive ? 'font-bold border-b-2 border-[#5843E5] focus:ring-0 focus:outline-none text-[#212121]' : 'text-[#666666]']">
            {{ useCurrencyFormat(null, item.total, null, false, true, true) }} {{ item.label }}
          </button>
        </Tab>
      </TabList>

      <TabPanels class="text-sm mt-4 lg:mt-6">
        <TabPanel class="focus:ring-0 focus:ring-offset-0 focus:outline-none">
          <SearchTabsJobs @set-meta="(value) => emit('setMeta', value)" @tab-total="(value) => setTotal(value, 0)" />
        </TabPanel>
        <TabPanel class="focus:ring-0 focus:ring-offset-0 focus:outline-none">
          <SearchTabsCompanies :curr-url="tabs[1].currUrl" @set-meta="(value) => emit('setMeta', value)" @tab-total="(value) => setTotal(value, 1)" />
        </TabPanel>
      </TabPanels>
    </TabGroup>
  </div>
</template>
